import React, { useState, useEffect, useContext } from "react";
import { OutboxContext } from "./main";
import { observer } from "mobx-react";
import { outboxStoreData } from "./outboxStore";
import MutliSelectDropdown from "./Dropdown/MultiSelectDropdown";
import ConnectInboxDropdown from "./Dropdown/ConnectInboxDropdown";
import ContactDateFilter from "Pages/Dashboard/contacts/ContactFilter/ContactDateFilter";
import AddMultiInputEmail from "./Dropdown/AddMultiInputEmail";
import Utils from "Utils/utils";
import userDetail from "Stores/userDetail";
import useDebouncedApply from "Utils/hooks/useDebouncedApply";

export const OutboxFilterByOption = observer(
  ({ applyFilterCbk = () => {}, resetFilterCbk = () => {} }) => {
    const {
      sequenceLoader = false,
      data = {},
      setShowFilter = () => {},
      setData = () => {},
      setAllEmailsSelectedCheckbox = () => {},
      setAllEmailsSelectedInView = () => {}
    } = useContext(OutboxContext);

    const [activeDropdown, setActiveDropdown] = useState(false);
    const [errMsg, setErrMsg] = useState("");

    // Action
    const clearAll = (actionType = "") => {
      if (
        outboxStoreData?.filterList?.length > 0 ||
        outboxStoreData?.tempFilterList?.length > 0
      ) {
        outboxStoreData.setActiveOutboxId("");
        outboxStoreData.setCurrentPage(1);
        let value = resetFilterCbk();
        if (value) {
          outboxStoreData?.filterList?.length > 0 &&
            applyFilterCbk(actionType || "clear");
          outboxStoreData.setTempFilterList([]);
          outboxStoreData.setFilterList([]);
        }
      }
      setAllEmailsSelectedCheckbox(false);
      setAllEmailsSelectedInView(false);
    };

    const cancelFilter = () => {
      // Filters auto-apply as you edit, so Close just dismisses the panel;
      // "Clear all" remains the explicit reset.
      setShowFilter(false);
    };

    const applyFilter = () => {
      if (
        outboxStoreData?.filterList?.length > 0 ||
        outboxStoreData?.tempFilterList?.length > 0
      ) {
        outboxStoreData.setCurrentPage(1);
        outboxStoreData.setFilterList([...outboxStoreData.tempFilterList]);
        outboxStoreData.setFilterObject({ ...data });
        applyFilterCbk(true);
      }
    };

    const actionTypeFuncObject = {
      clear: clearAll,
      apply: applyFilter,
      cancel: cancelFilter
    };

    const submitAction = (type = "") => {
      actionTypeFuncObject[type]();
    };

    // Dropdown Action
    const setFiterUsed = (isValidList = false, key = "") => {
      if (key) {
        let filterList = [...outboxStoreData?.tempFilterList];
        if (isValidList && !filterList.includes(key)) {
          filterList.push(key);
        } else if (!isValidList && filterList?.length > 0) {
          filterList = filterList.filter(
            item => item?.toLowerCase() !== key?.toLowerCase()
          );
        }
        outboxStoreData.setTempFilterList(filterList);
      }
    };

    // Only fully-formed selections count — same rule drives the auto-apply
    // signature so half-built rows never fire a query.
    const getValidItems = (key = "", value = []) => {
      if (key === "toAddress") {
        return value || [];
      }
      if (key === "dates") {
        return (
          (value || []).filter(
            item => item?.fieldName && item?.fromDate && item?.toDate
          ) || []
        );
      }
      return (value || []).filter(item => item?.checked) || [];
    };

    const getSelectedFilterKey = (key = "", value = []) => {
      if (key) {
        setFiterUsed(getValidItems(key, value)?.length > 0, key);
      }
    };

    // Auto-apply: re-query (debounced) whenever committed selections change.
    // applyFilter self-guards on filterList/tempFilterList length.
    const buildAppliedSignature = () =>
      JSON.stringify(
        Object.keys(data || {}).reduce((acc, key) => {
          acc[key] = getValidItems(key, data[key]);
          return acc;
        }, {})
      );
    useDebouncedApply(buildAppliedSignature(), applyFilter);

    const getSelectedItems = (item = []) => {
      if (item?.length > 0) {
        return item.filter(item => item?.checked) || [];
      }
      return [];
    };

    const handleFilterObject = (key = "", value = []) => {
      if (key) {
        getSelectedFilterKey(key, value);
        let tempObj = { ...data, [key]: value };
        setData({ ...tempObj });
      }
    };

    const handleActiveFilter = (value = "") => {
      setActiveDropdown(value === activeDropdown ? "" : value);
    };

    const getListArr = () => {
      return data?.listIds?.filter(item => item?.id) || [];
    };

    const selectedChannels = getSelectedItems(data?.channel);
    const isChannelEmailOnly =
      selectedChannels?.length === 1 && selectedChannels?.[0]?.id === "EMAIL";
    const hasMultiLiAccounts =
      (data?.liAccountId?.length || 0) > 1 || !!userDetail?.hasMultiLiAccountHistory;
    const showLiAccountFilter = hasMultiLiAccounts && !isChannelEmailOnly;

    // Dropdown List
    const dropdownList = [
      {
        label: "Owners",
        keyValue: "owners",
        placeholder: "Select",
        selected: getSelectedItems(data?.owners),
        data: data?.owners || [],
        setAction: handleFilterObject,
        activeFilter: activeDropdown,
        setActiveFilter: handleActiveFilter,
        type: "multiSelectApply",
        count: 2
      },
      {
        label: "Channel",
        keyValue: "channel",
        placeholder: "Select", // Select
        selected: getSelectedItems(data?.channel),
        data: data?.channel || [],
        setAction: handleFilterObject,
        activeFilter: activeDropdown,
        setActiveFilter: handleActiveFilter,
        type: "multiSelectApply",
        count: 2
      },
      {
        label: "From Address",
        keyValue: "fromAddress",
        placeholder: "Select",
        selected: getSelectedItems(data?.fromAddress),
        data: data?.fromAddress || [],
        setAction: handleFilterObject,
        activeFilter: activeDropdown,
        setActiveFilter: handleActiveFilter,
        type: "singleSelect"
      },
      ...(showLiAccountFilter
        ? [
            {
              label: "LinkedIn Account",
              keyValue: "liAccountId",
              placeholder: "Select",
              selected: getSelectedItems(data?.liAccountId),
              data: data?.liAccountId || [],
              setAction: handleFilterObject,
              activeFilter: activeDropdown,
              setActiveFilter: handleActiveFilter,
              type: "singleSelect"
            }
          ]
        : []),
      {
        label: "To Address",
        keyValue: "toAddress",
        selected: data?.toAddress,
        data: data?.toAddress || [],
        placeholder: "Enter recipient address",
        setAction: handleFilterObject,
        activeFilter: activeDropdown,
        setActiveFilter: handleActiveFilter,
        type: "multiInputSelect",
        autoComplete: true,
        count: 3
      },
      {
        label: "Sequences",
        keyValue: "sequences",
        placeholder: "Select",
        selected: getSelectedItems(data?.sequences),
        data: data?.sequences || [],
        setAction: handleFilterObject,
        activeFilter: activeDropdown,
        setActiveFilter: handleActiveFilter,
        type: "multiSelectApply",
        count: 2,
        loader: sequenceLoader
      },
      {
        label: "Email Type",
        keyValue: "emailType",
        placeholder: "Select",
        selected: getSelectedItems(data?.emailType),
        data: data?.emailType || [],
        setAction: handleFilterObject,
        activeFilter: activeDropdown,
        setActiveFilter: handleActiveFilter,
        type: "multiSelectApply",
        count: 4
      },
      {
        label: "Date",
        keyValue: "dates",
        data: data?.dates || [],
        activeFilter: activeDropdown,
        setActiveFilter: handleActiveFilter,
        setAction: handleFilterObject,
        errMsg: errMsg,
        setErrMsg: setErrMsg,
        type: "multiDateSelect"
      },
      {
        label: "Tags",
        keyValue: "tags",
        placeholder: "Select",
        selected: getSelectedItems(data?.tags),
        data: data?.tags || [],
        setAction: handleFilterObject,
        activeFilter: activeDropdown,
        setActiveFilter: handleActiveFilter,
        type: "multiSelectApply",
        count: 2
      },
      {
        label: "Contact Lists",
        keyValue: "listIds",
        placeholder: "Select", // Select
        selected: getSelectedItems(data?.listIds),
        data: getListArr(),
        setAction: handleFilterObject,
        activeFilter: activeDropdown,
        setActiveFilter: handleActiveFilter,
        type: "multiSelectApply",
        count: 2
      }
    ];

    const hasFilters =
      outboxStoreData?.filterList?.length > 0 ||
      outboxStoreData?.tempFilterList?.length > 0;
    const actionList = [
      {
        className: `clearAllFilter${hasFilters ? "" : " disabled"}`,
        name: "Clear All",
        id: "outboxClearFilter",
        type: "clear"
      },
      {
        className: "cancelFilter",
        name: "Close",
        id: "outboxCancelFilter",
        type: "cancel"
      }
    ];

    useEffect(() => {
      return () => {
        setActiveDropdown("");
        setErrMsg("");
      };
    }, []);

    useEffect(() => {
      if (!showLiAccountFilter && data?.liAccountId?.some(item => item?.checked)) {
        handleFilterObject(
          "liAccountId",
          (data.liAccountId || []).map(item => ({ ...item, checked: false }))
        );
      }
    }, [showLiAccountFilter]);

    return (
      <div className="outboxFilterByOption">
        <FilterHeader />
        <div className="filterBody">
          {dropdownList?.map(item => {
            const { keyValue = "", type = "" } = item || {};
            if (type === "singleSelect") {
              return <ConnectInboxDropdown key={keyValue} {...item} />;
            } else if (type === "multiInputSelect") {
              return <AddMultiInputEmail key={keyValue} {...item} />;
            } else if (type === "multiSelectApply") {
              return <MutliSelectDropdown key={keyValue} {...item} />;
            } else if (type === "multiDateSelect") {
              return (
                <ContactDateFilter key={keyValue} {...item} inlineOperator />
              );
            }
          })}
        </div>
        <FilterAction submitCbk={submitAction} actionList={actionList} />
      </div>
    );
  }
);

const FilterHeader = () => {
  // Clear all moved into the footer now that filters auto-apply.
  return (
    <div className="filterHeader">
      <span className="name">Filter</span>
    </div>
  );
};

const FilterAction = ({ actionList = [], submitCbk = () => {} }) => {
  const handleAction = (event = "") => {
    let actionType = Utils.performActionByClosestDom(event, "div", "data-type");
    actionType && submitCbk(actionType);
  };

  return (
    <div className="filterAction" onClick={e => handleAction(e)}>
      {(actionList?.length > 0 &&
        actionList.map((item, index) => {
          const { className = "", name = "", id = "", type = "" } = item || {};
          return (
            <div
              className={className}
              key={`${id}Action${index}`}
              data-type={type}
            >
              {name}
            </div>
          );
        })) ||
        ""}
    </div>
  );
};

export default OutboxFilterByOption;
